import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import { getCellReducers } from './helpers/getCellReducers';
import { useAccess } from '../../../hooks/useAccess';
import {
  SyncRuleDto,
  useCreateOrUpdateSyncRule,
  useDeleteSyncRule,
  useGetSyncRules,
} from '../../../services/redux/api/syncApi';

export function useSyncRulesComponent(classes) {
  const { t } = useTranslation();
  const { data: rules } = useGetSyncRules();
  const [createOrUpdateRule, { isLoading: isSaving }] =
    useCreateOrUpdateSyncRule();
  const [deleteRule, { isLoading: isDeleting }] = useDeleteSyncRule();
  const [filter, setFilter] = useState<string>('');
  const [itemForDelete, setItemForDelete] = useState<SyncRuleDto | null>(null);
  const [itemForEditing, setItemForEditing] = useState<SyncRuleDto | null>(
    null,
  );
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const { access } = useAccess();

  const { order: defaultOrder, orderBy } = getDefaultSettings(t);

  const filteredValues = useMemo(
    () =>
      rules?.filter((rule: SyncRuleDto) => {
        if (!filter) return true;

        const filterValue = filter.toLowerCase();

        if (
          rule.provider.toLowerCase().includes(filterValue) ||
          rule.entity.toLowerCase().includes(filterValue) ||
          rule.localPath.toLowerCase().includes(filterValue)
        ) {
          return true;
        }

        return false;
      }),
    [rules, filter],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    access?.syncRules?.canEdit ?? false,
    access?.syncRules?.canDelete ?? false,
    (rule: SyncRuleDto) => {
      setItemForEditing(rule);
      setShowEditModal(true);
    },
    (rule: SyncRuleDto) => {
      setItemForDelete(rule);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.APP_VARIABLE_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const { order, columnsList, onSortColumns, sortedArray, onSort } =
    useTableData;

  function onItemDelete() {
    if (!itemForDelete) return;
    deleteRule(itemForDelete.id).then(() => {
      setItemForDelete(null);
    });
  }

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    filter,
    setFilter,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    itemForEditing,
    setItemForEditing,
    showEditModal,
    setShowEditModal,
    isSaving,
    isDeleting,
    createOrUpdateRule,
  };
}
